import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Route, Router } from '@angular/router';
import { map } from 'rxjs';
import { Action, AppAbility, Subject } from './ability';
import { SessionService } from './session.service';

export interface RoutePermission {
  action: Action;
  subject: Subject;
}

const PERMISSION_DATA_KEY = 'permission';

/** Route `data` entry read by `permissionGuard` and the menu filter. */
export function requires(action: Action, subject: Subject): Record<string, RoutePermission> {
  return { [PERMISSION_DATA_KEY]: { action, subject } };
}

export function routePermission(route: Pick<Route, 'data'>): RoutePermission | undefined {
  return route.data?.[PERMISSION_DATA_KEY] as RoutePermission | undefined;
}

/** True while every activated route in the chain is still permitted; used after the ability changes at runtime. */
export function canRemainOn(root: ActivatedRouteSnapshot, ability: AppAbility): boolean {
  for (let route: ActivatedRouteSnapshot | null = root; route; route = route.firstChild) {
    const permission = routePermission(route);
    if (permission && !ability.can(permission.action, permission.subject)) {
      return false;
    }
  }
  return true;
}

export const permissionGuard: CanActivateFn = (route) => {
  const permission = routePermission(route);
  if (!permission) {
    return true;
  }

  const ability = inject(AppAbility);
  const router = inject(Router);

  return inject(SessionService)
    .load()
    .pipe(map(() => (ability.can(permission.action, permission.subject) ? true : router.createUrlTree(['/dashboard']))));
};
